import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'

import { permissionsList } from './constants'

const useGroup = ({ storeId, groupId, allowsShipments }) => {
  const { data: groupInfo, loading: loadingGroup } = useFetch(
    () => configurationServices.getUserGroup({ storeId, groupId }),
    {
      doInitialRequest: groupId !== 'new',
    }
  )

  const { fetchData: createUserGroup, loading: creatingUserGroup } = useFetch(configurationServices.createUserGroup, {
    doInitialRequest: false,
  })

  const { fetchData: editUserGroup, loading: editingUserGroup } = useFetch(configurationServices.editUserGroup, {
    doInitialRequest: false,
  })

  const { data: backendPermissions } = useFetch(() => configurationServices.getPermissions({ storeId }), {
    doInitialRequest: true,
  })

  const [hasChanges, setHasChanges] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState('')
  const [permissions, setPermissions] = useState([])
  const [defaultGroup, setDefaultGroup] = useState(false)
  const { t } = useTranslation()

  const setInitialValues = useCallback(() => {
    if (groupInfo) {
      setName(groupInfo?.name || '')
      setColor(groupInfo?.color || '')
      setPermissions(groupInfo?.permissions?.map(permission => permission.codename) || [])
      setDefaultGroup(groupInfo?.default || false)
      setHasChanges(false)
    }
  }, [groupInfo])

  useEffect(() => {
    setInitialValues()
  }, [groupInfo, setInitialValues])

  const revertChanges = () => {
    setInitialValues()
  }

  const storeOffersPermissions = ({ permissions: requiredPermissions }) =>
    !!backendPermissions &&
    requiredPermissions.every(({ value }) => backendPermissions.some(({ codename }) => codename === value))

  const getAvailablePermissions = () => {
    return permissionsList(t).filter(permission => {
      if (!allowsShipments && (permission.key === 'shipments' || permission.key === 'inventory')) return false

      return !permission.featureGated || storeOffersPermissions(permission)
    })
  }

  return {
    name,
    setName: param => {
      setHasChanges(true)
      setName(param)
    },
    color,
    setColor: param => {
      setHasChanges(true)
      setColor(param)
    },
    permissions,
    setPermissions: param => {
      setHasChanges(true)
      setPermissions(param)
    },
    usersCount: groupInfo?.users?.length || 0,
    hasChanges,
    setHasChanges,
    revertChanges,
    createUserGroup,
    creatingUserGroup,
    editUserGroup,
    editingUserGroup,
    defaultGroup,
    availablePermissions: getAvailablePermissions(),
    loadingGroup,
    backendPermissions,
  }
}

export default useGroup
